import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/Button';

/* **Light is the default and dark is the alternate**, reversed on 2026-08-24 with the design-system
 * change: `tokens.css` now defines the light palette on `:root` and overrides it under
 * `[data-theme='dark']`. The toggle's mechanism is unchanged — it writes `data-theme` on the root
 * either way, and `light` simply falls through to the base values.
 *
 * No new colours are introduced here.
 *
 * `localStorage`, not a cookie and not a server round trip: the choice is per-device and belongs to
 * the person, not the tenant. Read during the first render rather than in an effect, so there is no
 * flash of the wrong theme.
 *
 * `prefers-color-scheme` is respected only as the initial default. Once someone has chosen, their
 * choice wins — a control that silently reverts to the OS setting is a control that does not work.
 *
 * Both browser APIs are accessed defensively, and *not* with optional chaining, which the type
 * checker correctly called unnecessary: the DOM types say both are always present. The real failure
 * modes are different from absence.
 */

const STORAGE_KEY = 'lumiq.theme';
type Theme = 'dark' | 'light';

/** `localStorage` **throws** rather than being absent when storage is denied — Safari private
 * browsing, blocked cookies, a strict enterprise policy. A theme preference is not worth failing a
 * render over, so the whole access is guarded and a failure just means "no stored choice". */
function storedTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : null;
  } catch {
    return null;
  }
}

function remember(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* The toggle still works for this session; only the memory of it is lost. */
  }
}

/** jsdom does not implement `matchMedia`, so a component test would throw on it — a `typeof` check
 * rather than an optional chain, because the value is declared non-nullish and the guard is about
 * the function existing at runtime rather than being null.
 *
 * **Queries `dark` rather than `light`**, which is the half that matters: `prefers-color-scheme` has
 * three states, and the third is *no preference*. Asking for `light` treated "no preference" as a
 * request for dark; asking for `dark` treats it as light, which is the theme this product now
 * defaults to. The absent case has to fall to a stated default rather than to whichever question
 * happened to be asked. */
function prefersDark(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function initialTheme(): Theme {
  return storedTheme() ?? (prefersDark() ? 'dark' : 'light');
}

export function ThemeToggle(): ReactElement {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;
    remember(theme);
  }, [theme]);

  const next: Theme = theme === 'dark' ? 'light' : 'dark';

  return (
    <Button
      tone="secondary"
      onClick={() => {
        setTheme(next);
      }}
      /* The label names the *destination*, not the current state. "Dark mode" on a button is
       * ambiguous about whether it describes now or the result; "Switch to light" is not. */
      aria-label={t(`theme.switchTo.${next}`)}
      title={t(`theme.switchTo.${next}`)}
      size="compact"
    >
      {t(`theme.${next}`)}
    </Button>
  );
}
